import { useLocalStorage } from '@rehooks/local-storage';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { LOCAL_STORAGE_PREFIX, LocalStorageKeys } from '~/common/constants/localStorage';

export interface UseResizeWidthProps {
  minWidth: number | string;
  maxWidth: number | string;
  key?: LocalStorageKeys;
  parent?: Window | HTMLElement;
  anchor?: 'right' | 'left';
}

export interface UseResizeWidthReturn {
  width: number;
  isResizing: boolean;
  enableResize: () => void;
}

// Used to ensure cursor stays in resizing component
// to avoid css cursor type changes on resize handle
const CURSOR_OFFSET = 2;

const isNumber = (value: string | number): value is number => typeof value === 'number';
const parseDimension = (dimension: number | string, refDimension: number): number => {
  if (isNumber(dimension)) {
    return dimension;
  }
  if (dimension.endsWith('%')) {
    return (parseFloat(dimension.replace('%', '')) / 100) * refDimension;
  }
  if (dimension.endsWith('px')) {
    return parseInt(dimension.replace('px', ''), 10);
  }

  throw new Error('Dimension must be a number (pixel) or a string ending with "px" or "%');
};

/**
 * A React hook that provides width resizing functionality with local storage persistence.
 * Allows users to resize elements horizontally with mouse drag gestures and remembers
 * the width preference across browser sessions.
 *
 * Implemented to with Drawer Components to allow user controlled widths for side panels.
 *
 * @param props - Configuration object for the resize behavior
 * @param props.minWidth - Minimum allowed width. Can be a number (pixels), string with 'px' suffix, or percentage string (e.g., '20%')
 * @param props.maxWidth - Maximum allowed width. Can be a number (pixels), string with 'px' suffix, or percentage string (e.g., '80%')
 * @param props.key - Optional local storage key for persisting width preference. Defaults to LocalStorageKeys.RESIZE_DRAWER;
 *                    Helpful when persisting different widths per entities
 * @param props.parent - Optional parent element or window for calculating relative dimensions. Defaults to window
 * @param props.anchor - Optional anchor side for resize direction. 'left' resizes from left edge, 'right' from right edge. Defaults to 'left'
 *
 * @returns Object containing resize state and controls
 * @returns returns.width - Current width value in pixels
 * @returns returns.isResizing - Boolean indicating if resize operation is currently active
 * @returns returns.enableResize - Function to initiate resize mode (typically called on mousedown)
 *
 * @throws {Error} When dimension values are not valid numbers, pixel strings, or percentage strings
 */
export const useResizeWidth = ({
  key = LocalStorageKeys.RESIZE_DRAWER,
  minWidth,
  maxWidth,
  parent = window,
  anchor = 'left',
}: UseResizeWidthProps): UseResizeWidthReturn => {
  const parentWidth = parent instanceof Window ? window.innerWidth : parent.clientWidth;
  const minWidthNumber = useMemo(() => parseDimension(minWidth, parentWidth), [minWidth, parentWidth]);
  const maxWidthNumber = useMemo(() => parseDimension(maxWidth, parentWidth), [maxWidth, parentWidth]);

  const [isResizing, setIsResizing] = useState(false);
  const [currentWidth, setCurrentWidth] = useLocalStorage(`${LOCAL_STORAGE_PREFIX}${key}`, minWidthNumber);
  const [width, setWidth] = useState(currentWidth);

  const enableResize = useCallback(() => {
    setIsResizing(true);
    // Workaround to prevent random text selection while resizing
    document.body.style.userSelect = 'none';

    // snap back to maxWidth on resize start if current width is already at or above max
    if (currentWidth >= maxWidthNumber) {
      setWidth(maxWidthNumber);
      setCurrentWidth(maxWidthNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setIsResizing]);

  const disableResize = useCallback(() => {
    setIsResizing(false);
    // Restore deactivated text selection after resizing ends
    document.body.style.userSelect = 'unset';
  }, [setIsResizing]);

  const resize = useCallback(
    (e: MouseEvent) => {
      if (isResizing) {
        const newWidth = anchor === 'left' ? e.clientX : document.body.clientWidth - e.clientX;
        const newWidthWithOffset = newWidth + CURSOR_OFFSET;
        if (newWidthWithOffset >= minWidthNumber && newWidthWithOffset <= maxWidthNumber) {
          setWidth(newWidthWithOffset);
          setCurrentWidth(newWidthWithOffset);
        }
      }
    },
    [isResizing, anchor, minWidthNumber, maxWidthNumber, setCurrentWidth],
  );

  useEffect(() => {
    // document.
    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', disableResize);

    return () => {
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', disableResize);
    };
  }, [disableResize, resize]);

  return {
    // enforcing the width can not exeed maxWidthNumber in case parent width changed
    // does not interfere with user set width unless explicitely resized manually again
    width,
    isResizing,
    enableResize,
  };
};
